import { useCallback, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import {
  AllDoneFill,
  ArrowBackOutline,
  ClockFuture,
  ConnectingDots,
  DollarInCircleOutline,
  DollarWithClockOutline,
  EditOutline,
  EngagementOutline,
  ExcalamationInSquareOutline,
  FileTextOutline,
  GlobeOutline,
  KeyOutline2,
  PersonWith2Stars,
  PersonWith3Stars,
  PieWithPercent,
  RecycleOutline,
  SettingsInfo,
} from '@lyearn/icons';
import { Avatar, Button, Loader, Stack, Tabs, Typography } from '@lyearn/ui';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import useNavigation from '@/hooks/useNavigation';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import { useQuestionsQuery } from '@/modules/corporate/modules/questions/views/AddQuestionModal/graphql/queries/generated/questions';
import SolutionCardGrid from '@/modules/knowledge-asset/KnowledgeAssetManager/components/SolutionCardGrid';
import TemplatesHeaderNode from '@/modules/templates/views/components/TemplatesHeaderNode';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  KnowledgeAssetContentType,
  ResourceStatusEnum,
  UserSurveyResponseVersionStatusEnum,
} from '@/types/schema';
import { ContentApprovalStatusEnum } from '@/types/schema';

import { useKnowledgeAssetsQuery } from '../../KnowledgeAssetManager/graphql/query/generated/knowledgeAssets';
import { logoStyle } from '../../KnowledgeAssetManager/styles';
import Disclaimer from '../components/Disclaimer';
import EmptyState from '../components/EmptyState';
import { usePersonaQuery } from '../graphql/queries/generated/persona';
import { usePersonaDetailsQuery } from '../graphql/queries/generated/personaDetails';
import { usePersonaQuestionIdsQuery } from '../graphql/queries/generated/personaQuestionIds';
import useApproveContent from '../hooks/useApproveContent';
import { getResponseByQuestionText } from '../SolutionDetails/utils';
import { DetailsPageProps } from '../types';

enum CustomerStoryTab {
  BUSINESS_CHALLENGES = 'business_challenges',
  BUSINESS_OUTCOMES = 'business_outcomes',
  SOLUTIONS_IMPLEMENTATION = 'solutions_implementation',
  LOOKING_FORWARD = 'looking_forward',
}

const parsePipeSeparatedString = (text: string): string[] => {
  if (!text) return [];
  return text
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
};

const CustomerStoryDetails = ({ assetId, isPreview }: DetailsPageProps) => {
  const { isMobile } = useScreen();
  const [currentTab, setCurrentTab] = useState<CustomerStoryTab>(
    CustomerStoryTab.BUSINESS_CHALLENGES,
  );
  const isAdminRoute = useAdminRoute();
  const [{ data: personaData, fetching: fetchingPersonaData }] = usePersonaQuery({
    variables: {
      filters: {
        id: assetId,
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const isDraft =
    (personaData?.content as KnowledgeAssetContentType)?.status === ResourceStatusEnum.Draft;

  const { onApprove, fetching: isApproving } = useApproveContent({ contentId: assetId });

  const showApproveButton =
    (personaData?.content as KnowledgeAssetContentType)?.approvalStatus ===
      ContentApprovalStatusEnum.InReview &&
    (personaData?.content as KnowledgeAssetContentType)?.status === ResourceStatusEnum.Published;

  const attemptIds =
    (personaData?.content as any)?.userSurveyAttemptConfig?.map(
      (attempt: any) => attempt?.attemptId,
    ) || [];

  const userSurveyIds = (personaData?.content as any)?.userSurveyAttemptConfig?.map(
    (attempt: any) => attempt?.userSurveyId,
  );

  const [{ data: personaDetailsData, fetching: fetchingPersonaDetailsData }] =
    usePersonaDetailsQuery({
      variables: {
        filters: {
          attemptIds,
          ...(isPreview ? { versionStatus: UserSurveyResponseVersionStatusEnum.Draft } : {}),
        },
        first: -1,
      },
      pause: !attemptIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const [{ data: personaQuestionsData, fetching: fetchingUserSurveyDetailsData }] =
    usePersonaQuestionIdsQuery({
      variables: {
        filters: {
          ids: userSurveyIds,
        },
      },
      pause: !userSurveyIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const questionIds =
    personaQuestionsData?.userSurveys?.edges?.flatMap(
      (edge) =>
        edge?.node?.questionConfig?.surveyQuestions?.map(
          (question) => question?.questionId || '',
        ) || [],
    ) || [];

  const [{ data: questionsData, fetching: fetchingQuestionsData }] = useQuestionsQuery({
    variables: {
      filters: {
        ids: questionIds,
      },
    },
    pause: !questionIds?.length,
    requestPolicy: 'cache-and-network',
  });

  // Update question text keys for customer story
  const companyName = getResponseByQuestionText('Company Name', questionsData, personaDetailsData);
  const companyLogo = getResponseByQuestionText('Company Logo', questionsData, personaDetailsData);
  const industries = parsePipeSeparatedString(
    getResponseByQuestionText('Industry', questionsData, personaDetailsData),
  );
  const productServiceFocus = getResponseByQuestionText(
    'Product or Service Focus',
    questionsData,
    personaDetailsData,
  );
  const geographicPresence = getResponseByQuestionText(
    'Geographic Presence',
    questionsData,
    personaDetailsData,
  );
  const marketPositionSize = getResponseByQuestionText(
    'Market Position',
    questionsData,
    personaDetailsData,
  );
  const sizeAndScale = getResponseByQuestionText(
    'Size And Scale',
    questionsData,
    personaDetailsData,
  );

  const background = getResponseByQuestionText('Background', questionsData, personaDetailsData);
  const coreValues = getResponseByQuestionText('Core values', questionsData, personaDetailsData);

  // Add these to the existing response fetching section
  const painPoints = parsePipeSeparatedString(
    getResponseByQuestionText('Pain Points', questionsData, personaDetailsData),
  );
  const dataSystemIssues = parsePipeSeparatedString(
    getResponseByQuestionText(
      'Data, System & Operational Issues',
      questionsData,
      personaDetailsData,
    ),
  );
  const customerExperienceLimitations = parsePipeSeparatedString(
    getResponseByQuestionText('Customer Experience Limitations', questionsData, personaDetailsData),
  );
  const salesGrowth = getResponseByQuestionText('Sales Growth', questionsData, personaDetailsData);
  const returnOnInvestment = getResponseByQuestionText(
    'Return on Investment',
    questionsData,
    personaDetailsData,
  );
  const lifeTimeValue = getResponseByQuestionText(
    'Life Time Value',
    questionsData,
    personaDetailsData,
  );
  const processImprovements = getResponseByQuestionText(
    'Process Improvements',
    questionsData,
    personaDetailsData,
  );
  const workflowEnhancements = getResponseByQuestionText(
    'Workflow Enhancements',
    questionsData,
    personaDetailsData,
  );
  const customerExperience = getResponseByQuestionText(
    'Customer Experience',
    questionsData,
    personaDetailsData,
  );
  const engagementMetrics = getResponseByQuestionText(
    'Engagement Metrics',
    questionsData,
    personaDetailsData,
  );

  // Add this to the existing response fetching section
  const futureInitiatives = getResponseByQuestionText(
    'Future Business Initiatives',
    questionsData,
    personaDetailsData,
  );

  const solutionWinSalesforceLinksCombined = getResponseByQuestionText(
    'Salesforce Link',
    questionsData,
    personaDetailsData,
  );

  const solutionWinSalesforceLinks = solutionWinSalesforceLinksCombined
    ? solutionWinSalesforceLinksCombined
        .split('|')
        .map((link) => link.trim())
        .filter(Boolean)
    : [];

  // Add this to the existing response fetching section
  const solutionResponse = getResponseByQuestionText('Solution', questionsData, personaDetailsData);
  const solutionIds = solutionResponse
    ? solutionResponse
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

  const [{ data: solutionsData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: solutionIds,
      },
    },
    pause: !solutionIds?.length,
    requestPolicy: 'cache-and-network',
  });

  // Add this to the existing response fetching section
  const solutionKeyComponents = parsePipeSeparatedString(
    getResponseByQuestionText('Solution Key Component', questionsData, personaDetailsData),
  );

  const isLoading =
    fetchingPersonaData ||
    fetchingPersonaDetailsData ||
    fetchingUserSurveyDetailsData ||
    fetchingQuestionsData;

  const pageTitle = (personaData?.content as any)?.name;

  const navigation = useNavigation();
  const handleEditClick = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/customer-story/${assetId}/edit`);
  }, [assetId, navigation]);

  const onBack = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/customer-story`);
  }, [navigation]);

  usePageLayout({
    title: pageTitle,
    LeftNode: <TemplatesHeaderNode isPreview={isPreview} title={pageTitle} onBack={onBack} />,
    RightNode:
      isPreview || isMobile ? null : (
        <Stack direction="row" gapX={8}>
          {showApproveButton ? (
            <Button
              StartIcon={AllDoneFill}
              loading={isApproving}
              variant="outlined"
              onClick={onApprove}>
              Approve
            </Button>
          ) : null}
          <Button StartIcon={EditOutline} variant="outlined" onClick={handleEditClick}>
            Edit Customer Story
          </Button>
        </Stack>
      ),
    showHeader: !isMobile,
  });

  const onbackPress = useCallback(() => {
    // navigation.replace(routes.LibraryPortal(), { appendQuery: false });
    navigation.back();
  }, [navigation]);

  const tabs = [
    {
      label: 'Business Challenges',
      value: CustomerStoryTab.BUSINESS_CHALLENGES,
    },
    {
      label: 'Business Outcomes',
      value: CustomerStoryTab.BUSINESS_OUTCOMES,
    },
    {
      label: 'Solutions Implementation',
      value: CustomerStoryTab.SOLUTIONS_IMPLEMENTATION,
    },
    {
      label: 'Looking Forward',
      value: CustomerStoryTab.LOOKING_FORWARD,
    },
  ];

  if (isLoading) {
    return (
      <Stack alignItems="center" className="h-[calc(100vh-64px)] w-full" justifyContent="center">
        <Loader size={40} />
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="center"
      className="mt-48 mb-128 max-w-[900px] mx-auto xs:min-w-full xs:p-12 xs:mt-12"
      direction="col"
      gapY={32}>
      {isAdminRoute ? null : (
        <Stack className="w-full">
          <ArrowBackOutline className="h-24 w-24 cursor-pointer" onClick={onbackPress} />
        </Stack>
      )}
      <Disclaimer className="-mb-16" />
      {/* Header Grid */}
      <Stack className="grid grid-cols-3 gap-16 w-full xs:grid-cols-1">
        {/* Company Info Card */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
          <Stack className="flex justify-between items-start">
            {/* Left side with logo and company info */}
            <Stack direction="col" gapY={16}>
              {companyLogo ? (
                <Avatar className={logoStyle} size={56} src={companyLogo} />
              ) : (
                <EmptyState height="h-[56px]" />
              )}
              <Stack direction="col" gapY={4}>
                <Typography numberOfLines={1} variant="heading-m">
                  {industries.length > 0 ? industries.join(', ') : 'N/A'}
                </Typography>
                <Typography className="text-text-secondary" variant="body-short-02">
                  {marketPositionSize || 'N/A'}
                </Typography>
                <Typography className="text-text-secondary" variant="body-short-02">
                  {sizeAndScale || 'N/A'}
                </Typography>
              </Stack>
            </Stack>

            {/* Right side with focus and presence */}
            <Stack className="text-right" direction="col" gapY={16}>
              <Typography className="text-text-secondary" variant="body-short-02">
                {productServiceFocus || 'N/A'}
              </Typography>
              <Typography className="text-text-secondary mt-auto" variant="body-short-02">
                {geographicPresence || 'N/A'}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {/* Background Card */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={12}>
          <Stack alignItems="center" gapX={8}>
            <FileTextOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-s">Background</Typography>
          </Stack>
          {background ? (
            <Typography className="text-text-secondary" variant="body-short-02">
              {background}
            </Typography>
          ) : (
            <EmptyState />
          )}
        </Stack>

        {/* Core Values Card */}
        <Stack className="border border-border rounded-12 p-20" direction="col" gapY={12}>
          <Stack alignItems="center" gapX={8}>
            <FileTextOutline fillColorToken="icon-active" height={24} width={24} />
            <Typography variant="heading-s">Core Values</Typography>
          </Stack>
          {coreValues ? (
            <Typography className="text-text-secondary" variant="body-short-02">
              {coreValues}
            </Typography>
          ) : (
            <EmptyState />
          )}
        </Stack>
      </Stack>

      {/* Tabs */}
      <Stack className="self-start w-full">
        <Tabs tabs={tabs} value={currentTab} onChange={setCurrentTab} />
      </Stack>

      {/* Content based on selected tab */}
      {currentTab === CustomerStoryTab.BUSINESS_CHALLENGES && (
        <Stack className="w-full grid grid-cols-2 gap-16 xs:grid-cols-1" direction="col">
          {/* Pain Points Section */}
          <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
            <Stack alignItems="center" gapX={8}>
              <ExcalamationInSquareOutline fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">Pain Points</Typography>
            </Stack>
            <Stack direction="col" gapY={8}>
              {painPoints.length > 0 ? (
                painPoints.map((point, index) => (
                  <Typography key={index} className="text-text-secondary" variant="body-long-01">
                    • {point}
                  </Typography>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>

          {/* Data, System & Operational Issues Section */}
          <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
            <Stack alignItems="center" gapX={8}>
              <SettingsInfo fill="transparent" height={24} width={24} />
              <Typography variant="heading-xs">Data, Systems & Operational Issue</Typography>
            </Stack>
            <Stack direction="col" gapY={8}>
              {dataSystemIssues.length > 0 ? (
                dataSystemIssues.map((issue, index) => (
                  <Typography key={index} className="text-text-secondary" variant="body-long-01">
                    • {issue}
                  </Typography>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>

          {/* Customer Experience Limitations Section */}
          <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
            <Stack alignItems="center" gapX={8}>
              <PersonWith2Stars fillColorToken="icon-active" height={24} width={24} />
              <Typography variant="heading-xs">Customer Experience Limitations</Typography>
            </Stack>
            <Stack direction="col" gapY={8}>
              {customerExperienceLimitations.length > 0 ? (
                customerExperienceLimitations.map((limitation, index) => (
                  <Typography key={index} className="text-text-secondary" variant="body-long-01">
                    • {limitation}
                  </Typography>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        </Stack>
      )}

      {currentTab === CustomerStoryTab.SOLUTIONS_IMPLEMENTATION && (
        <Stack className="w-full" direction="col" gapY={16}>
          {(solutionsData?.contents?.edges?.length ?? 0) > 0 && (
            <>
              <Typography variant="display-s">Solutions</Typography>
              <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
                {solutionsData?.contents?.edges?.map((edge, index) => (
                  <SolutionCardGrid
                    key={index}
                    hideActionMenu
                    index={index}
                    solution={edge?.node as ContentsFragment}
                  />
                ))}
              </Stack>
            </>
          )}

          <Typography className="mt-32" variant="display-s">
            Key Components
          </Typography>
          <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
            <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
              <Stack alignItems="center" gapX={8}>
                <KeyOutline2 fillColorToken="icon-active" height={24} width={24} />
                <Typography variant="heading-xs">Key Components</Typography>
              </Stack>
              <Stack direction="col" gapY={8}>
                {solutionKeyComponents.length > 0 ? (
                  solutionKeyComponents.map((component, index) => (
                    <Typography key={index} className="text-text-secondary" variant="body-long-01">
                      • {component}
                    </Typography>
                  ))
                ) : (
                  <EmptyState />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}

      {currentTab === CustomerStoryTab.BUSINESS_OUTCOMES && (
        <Stack className="w-full" direction="col" gapY={32}>
          {/* Revenue Impact Section */}
          <Stack direction="col" gapY={16}>
            <Typography variant="display-s">Revenue Impact</Typography>
            <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
              <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
                <Stack alignItems="center" gapX={8}>
                  <PieWithPercent fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">Sales Growth</Typography>
                </Stack>
                {salesGrowth ? (
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {salesGrowth}
                  </Typography>
                ) : (
                  <EmptyState />
                )}
              </Stack>

              <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
                <Stack alignItems="center" gapX={8}>
                  <DollarInCircleOutline fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">Return on Investment</Typography>
                </Stack>
                {returnOnInvestment ? (
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {returnOnInvestment}
                  </Typography>
                ) : (
                  <EmptyState />
                )}
              </Stack>

              <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
                <Stack alignItems="center" gapX={8}>
                  <DollarWithClockOutline fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">Life Time Value</Typography>
                </Stack>
                {lifeTimeValue ? (
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {lifeTimeValue}
                  </Typography>
                ) : (
                  <EmptyState />
                )}
              </Stack>
            </Stack>
          </Stack>

          {/* Operation Efficiency Section */}
          <Stack direction="col" gapY={16}>
            <Typography variant="display-s">Operation Efficiency</Typography>
            <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
              <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
                <Stack alignItems="center" gapX={8}>
                  <RecycleOutline fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">Process Improvements</Typography>
                </Stack>
                {processImprovements ? (
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {processImprovements}
                  </Typography>
                ) : (
                  <EmptyState />
                )}
              </Stack>

              <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
                <Stack alignItems="center" gapX={8}>
                  <ConnectingDots fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">Workflow Enhancements</Typography>
                </Stack>
                {workflowEnhancements ? (
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {workflowEnhancements}
                  </Typography>
                ) : (
                  <EmptyState />
                )}
              </Stack>
            </Stack>
          </Stack>

          {/* Customer Experience Section */}
          <Stack direction="col" gapY={16}>
            <Typography variant="display-s">Customer Experience</Typography>
            <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
              <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
                <Stack alignItems="center" gapX={8}>
                  <PersonWith3Stars fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">Customer Experience</Typography>
                </Stack>
                {customerExperience ? (
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {customerExperience}
                  </Typography>
                ) : (
                  <EmptyState />
                )}
              </Stack>

              <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
                <Stack alignItems="center" gapX={8}>
                  <EngagementOutline fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">Engagement Metrics</Typography>
                </Stack>
                {engagementMetrics ? (
                  <Typography className="text-text-secondary" variant="body-long-01">
                    {engagementMetrics}
                  </Typography>
                ) : (
                  <EmptyState />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}

      {currentTab === CustomerStoryTab.LOOKING_FORWARD && (
        <Stack className="w-full" direction="col" gapY={32}>
          <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
            {/* Future Initiatives Section */}
            <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
              <Stack alignItems="center" gapX={8}>
                <ClockFuture fillColorToken="icon-active" height={24} width={24} />
                <Typography variant="heading-xs">Future Business Initiatives</Typography>
              </Stack>
              {futureInitiatives ? (
                <Typography className="text-text-secondary" variant="body-long-01">
                  {futureInitiatives}
                </Typography>
              ) : (
                <EmptyState />
              )}
            </Stack>

            <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
              <Stack alignItems="center" direction="row" gapX={12} justifyContent="between">
                <Stack alignItems="center" gapX={8}>
                  <GlobeOutline fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">Salesforce Link</Typography>
                </Stack>
              </Stack>
              <Stack className="flex-1 mt-24 overflow-hidden" direction="col">
                {solutionWinSalesforceLinks.length > 0 ? (
                  solutionWinSalesforceLinks.map((link, index) => (
                    <Stack
                      key={index}
                      className={`cursor-pointer group pb-12 mb-12 w-full ${
                        index !== solutionWinSalesforceLinks.length - 1
                          ? 'border-b border-border'
                          : ''
                      }`}
                      onClick={() => window.open(link, '_blank')}>
                      <Typography
                        className="text-link-external group-hover:text-link-external-hover"
                        variant="body-long-01">
                        {link}
                      </Typography>
                    </Stack>
                  ))
                ) : (
                  <EmptyState />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default CustomerStoryDetails;
